import { hash } from '@breathelife/hash';
import { ESignSigner2FAInfo, ESignSignerAuthMethod, Signer } from '@breathelife/types';
import { PdfDocumentType } from '@zinnia/form-engine-sdk';

export function participantProcessor(
  participantSignerData: Partial<Record<PdfDocumentType, Signer[]>>,
): ESignSigner2FAInfo[] {
  const signerMap = new Map<string, ESignSigner2FAInfo>();

  let pdfDocumentType: keyof typeof participantSignerData;

  for (pdfDocumentType in participantSignerData) {
    const signerDataForDocumentType = participantSignerData[pdfDocumentType];
    signerDataForDocumentType?.forEach((participantSigner) => {
      const partyId = partyIdFromPartyData(participantSigner);
      let signer = signerMap.get(partyId);

      if (signer) {
        if (!signer.documentTypes) {
          signer.documentTypes = [];
        }
        signer.documentTypes.push(pdfDocumentType);
        // The backend narrows a signer's roles to only the ones that must sign each document type, so the same
        // party can arrive with a different role subset per document type (ex: INSURED/OWNER_PERSON on the
        // application and PAYER on the PAD). Union them, otherwise the first document type's subset wins and the
        // signature field for the dropped role never renders on the other document, leaving the party as a viewer.
        signer.roles = mergeRoles(signer.roles, participantSigner.roles);
      } else if (participantSigner.party?.person) {
        const {
          party: {
            person: { firstName, lastName },
            communication,
          },
          roles,
        } = participantSigner;

        const email = communication?.email;
        const phoneNumber = communication?.phoneNumber;

        signer = {
          partyId,
          firstName: firstName || '',
          lastName: lastName || '',
          email: email || '',
          cellphone: phoneNumber || '',
          authMethod: ESignSignerAuthMethod.cellphone,
          roles,
          documentTypes: [pdfDocumentType],
        };
      } else if (participantSigner.party?.organization) {
        const {
          party: {
            organization: { legalEntityName, legalEntityOwnerName },
            communication,
          },
          roles,
        } = participantSigner;

        const email = communication?.email;
        const phoneNumber = communication?.phoneNumber;

        signer = {
          partyId,
          firstName: legalEntityName || '',
          lastName: legalEntityOwnerName || '',
          email: email || '',
          cellphone: phoneNumber || '',
          authMethod: ESignSignerAuthMethod.cellphone,
          roles,
          documentTypes: [pdfDocumentType],
        };
      }
      if (signer) signerMap.set(partyId, signer);
    });
  }

  return Array.from(signerMap.values());
}

// Roles are deduplicated on both type and position, since a party can legitimately hold the same role on several
// document types (ex: the 1st agent signs the application, the advisor's report and the PAD). The position is part
// of the key because it identifies which role slot the party fills, and is matched on downstream to resolve a
// signer's signature fields
function mergeRoles(
  currentRoles: ESignSigner2FAInfo['roles'],
  additionalRoles: ESignSigner2FAInfo['roles'],
): ESignSigner2FAInfo['roles'] {
  const roleKey = (role: ESignSigner2FAInfo['roles'][number]): string => `${role.type}#${role.position}`;

  const mergedRoles = [...currentRoles];
  const mergedRoleKeys = new Set(currentRoles.map(roleKey));

  additionalRoles.forEach((additionalRole) => {
    if (!mergedRoleKeys.has(roleKey(additionalRole))) {
      mergedRoleKeys.add(roleKey(additionalRole));
      mergedRoles.push(additionalRole);
    }
  });

  return mergedRoles;
}

// Todo: enhance the id with more user data or a UUID - https://breathelife.atlassian.net/browse/HOT-3671
function partyIdFromPartyData(participantSigner: Signer): string {
  return (
    participantSigner.partyId ||
    hash(
      `${participantSigner.party.person?.firstName}-${participantSigner.party.person?.lastName}-${participantSigner.party.person?.dob}`,
    )
  );
}
